/**
 * frame-policy.ts
 *
 * What a widget page is allowed to load and reach, expressed as the Content
 * Security Policy that buildDoc stamps into every widget frame.
 *
 * Widening ALLOWED_SCRIPT_HOSTS widens what arbitrary generated code may pull
 * into a frame that can already read the app's SPARQL results, so the list is
 * code and changes by pull request. It is deliberately not a widget config
 * field: the page author is the untrusted party here and must not be able to
 * widen their own allow-list.
 *
 * The same list drives the build-time check in the review prototype
 * (wistor-dev/code-widget-control-solution/engine.js, DEFAULT + cspFor). The
 * two are kept in the same shape so a drift is visible in a diff.
 */

export const ALLOWED_SCRIPT_HOSTS = ['cdnjs.cloudflare.com', 'cdn.jsdelivr.net'] as const

/**
 * Hosts a page may load images from and nothing else. An image request is a way out only to whoever
 * reads that server's logs, so a host belongs here when that is an operator we trust and nobody else -
 * a public data service, never a host where anyone can run a server.
 */
export const ALLOWED_IMAGE_ONLY_HOSTS = [
  /* PDOK, the Dutch government's open geodata service run by Kadaster: background maps, aerial photos, BGT. */
  'service.pdok.nl',
] as const

/**
 * Everything img-src allows beyond data: and blob:. The library CDNs are in it because libraries ship
 * images of their own - Leaflet's marker icons, icon sets - and the same reasoning holds: only the CDN
 * reads its logs. The analyser reads this list too, so an <img> the frame lets through is not reported
 * as a leak.
 */
export const ALLOWED_IMAGE_HOSTS = [...ALLOWED_SCRIPT_HOSTS, ...ALLOWED_IMAGE_ONLY_HOSTS] as const

/**
 * The frame's CSP, as the value of a `<meta http-equiv>` content attribute.
 *
 * `'self'` appears nowhere on purpose. The frame is sandboxed without
 * allow-same-origin, so its origin is opaque and `'self'` matches nothing --
 * every host has to be named in full, and a policy copied from an ordinary app
 * would block everything.
 *
 * `hosts` is a parameter so the policy can be exercised without a DOM.
 */
export function widgetFrameCsp(
  hosts: readonly string[] = ALLOWED_SCRIPT_HOSTS,
  imageHosts: readonly string[] = ALLOWED_IMAGE_HOSTS
): string {
  const origins = hosts.map((host) => `https://${host}`).join(' ')
  const imageOrigins = imageHosts.map((host) => ` https://${host}`).join('')

  return [
    `default-src 'none'`,

    /*
     * The bootstrap and every generated page are inline scripts, so
     * 'unsafe-inline' stays for now. A nonce is stronger and buildDoc is the
     * right place to mint one, but it would also have to stamp the nonce onto
     * the page's own <script> tags -- a second change, not this one.
     *
     * 'unsafe-eval' is absent and should stay absent: it makes the browser
     * refuse eval, new Function and setTimeout("string"), which matches the
     * platform's existing position that dynamic.eval is a blocking finding.
     * Note what 'unsafe-inline' still permits: an injected event handler runs,
     * so this policy is not an XSS defence and the escaping rules carry that.
     */
    `script-src 'unsafe-inline' ${origins}`,

    /*
     * Workers from blob: only - code the page built itself, which it could run inline anyway. pdf.js and
     * vector-map libraries need one. A worker keeps this policy, so connect-src 'none' still holds inside
     * it. Without the directive the browser falls back to script-src, which has no blob:, and refuses.
     */
    `worker-src blob:`,
    `style-src 'unsafe-inline' ${origins}`,
    `font-src data: ${origins}`,

    /*
     * readFile hands bytes over postMessage and pages turn them into blob URLs
     * for images and attachments. object-src governs <embed> and <object>, and
     * blob: keeps a blob-backed image or SVG working through them. It is not
     * what makes a PDF preview work: Chrome declines to instantiate its PDF
     * viewer inside a sandboxed frame at all, with or without a policy, and
     * reports no object-src violation when it does so.
     */
    `img-src data: blob:${imageOrigins}`,
    `media-src data: blob:`,
    `object-src blob:`,
    `frame-src blob:`,

    /*
     * The bridge is postMessage, which CSP does not govern, so every wistor.*
     * call keeps working. What stops is the page opening its own fetch, XHR,
     * WebSocket or sendBeacon -- the one capability nothing else caps.
     */
    `connect-src 'none'`,
    `form-action 'none'`,
    `base-uri 'none'`,
  ].join('; ')
}

/**
 * The policy as a meta element, for injection at the top of the frame document.
 *
 * Browsers enforce every CSP a document carries and a resource has to satisfy
 * all of them, so a generated page shipping its own policy can only narrow
 * this one. That is what makes stamping it into page-supplied HTML safe.
 */
export function widgetFrameCspMeta(): string {
  return `<meta http-equiv="Content-Security-Policy" content="${widgetFrameCsp()}">`
}
